import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Modal } from '@safetyhub/ui';
import { formatPhone, normalizePhone } from '@safetyhub/shared';
import { MODAL_LAYER } from '../../../../components/modalLayers';
import {
  memberDedupApi,
  MemberDedupFieldChoices,
  MemberDedupPair,
} from '../../../../lib/memberDedup';
import { extractApiError } from '../../../../lib/organizations';
import { useToastStore } from '../../../../stores/toast';

const MATCHED_BY_LABEL: Record<MemberDedupPair['matchedBy'], string> = {
  phone: '전화번호 일치',
  email: '이메일 일치',
  name: '이름 일치',
};

/**
 * 이번에 처리한 페어의 결과 — 모달이 열려 있는 동안만 유지한다.
 *
 * 병합 건은 `duplicateMemberId`(흡수된 쪽)를 들고 있어야 되돌리기가 가능하다. 서버 unmerge는
 * "흡수된 행의 redirect 링크 해제"라 대표(target) id로는 되돌릴 수 없다.
 */
type ProcessedEntry =
  | { kind: 'merged'; duplicateMemberId: string }
  | { kind: 'dismissed' };

interface MemberDedupModalProps {
  open: boolean;
  onClose: () => void;
  /** 클레임한 자기 조직 회사 id — 대표 1차 판정의 스코프 */
  companyId: string;
  /** 병합/별개 처리 후 멤버 목록 갱신 (병합이 멤버십을 닫는다) */
  onChanged?: () => void;
  /** 모달 층은 여는 쪽이 정한다 — 멤버 관리 페이지에서 첫 모달이라 기본 root */
  overlayZClassName?: string;
}

/**
 * 중복 멤버 판정 모달 (P5 대표 1차 판정).
 *
 * 화면에 싣는 것은 신원 필드(이름·전화·이메일)뿐이다 — 그 사람이 어떤 현장·점검에 쓰였는지는
 * 조직의 업무 기록이라 대표에게도 비노출(결정 2026-08-14 §4 불변식 3). 서버 계약(shared
 * MemberDedupPair)이 애초에 신원 외 필드를 내려주지 않는다.
 *
 * 병합 방향: 계정 연결(hasAccount) 인원은 흡수되는 쪽이 될 수 없어(계정 신원은 본인만 판정)
 * 대표로 고정한다. 둘 다 무연결이면 대표가 어느 쪽을 남길지 선택한다.
 */
export function MemberDedupModal({
  open,
  onClose,
  companyId,
  onChanged,
  overlayZClassName = MODAL_LAYER.root,
}: MemberDedupModalProps) {
  const { showToast } = useToastStore();
  const [pairs, setPairs] = useState<MemberDedupPair[]>([]);
  /** 서버 반환 상한 초과 여부 — 일부 처리 후 재조회하면 다음 분이 이어서 나온다 */
  const [truncated, setTruncated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  /** pairKey → 대표로 남길 member id */
  const [targetChoice, setTargetChoice] = useState<Record<string, string>>({});
  /** pairKey → 충돌 필드 선택값 */
  const [fieldChoice, setFieldChoice] = useState<Record<string, MemberDedupFieldChoices>>({});
  const [busyPairKey, setBusyPairKey] = useState<string | null>(null);
  /**
   * pairKey → 이번 세션에 처리한 결과. 처리된 페어를 목록에서 곧바로 지우지 않고 남겨,
   * 병합 건은 그 자리에서 되돌릴 수 있게 한다(모달을 닫았다 열면 서버 조회 결과대로 사라진다).
   */
  const [processed, setProcessed] = useState<Record<string, ProcessedEntry>>({});

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await memberDedupApi.listCandidates(companyId);
      const list = result.pairs;
      setPairs(list);
      setTruncated(result.truncated);
      // 기본 대표: 계정 연결 인원 우선, 없으면 먼저 등록된(첫) 인원
      const defaults: Record<string, string> = {};
      for (const pair of list) {
        const withAccount = pair.members.find((m) => m.hasAccount);
        defaults[pair.pairKey] = (withAccount ?? pair.members[0]).id;
      }
      setTargetChoice(defaults);
      setFieldChoice({});
      setProcessed({});
    } catch (err) {
      showToast(extractApiError(err).message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [companyId, showToast]);

  useEffect(() => {
    if (open) {
      void load();
    }
  }, [open, load]);

  const markProcessed = (pairKey: string, entry: ProcessedEntry) => {
    setProcessed((prev) => ({ ...prev, [pairKey]: entry }));
    onChanged?.();
  };

  const handleMerge = async (pair: MemberDedupPair) => {
    const targetId = targetChoice[pair.pairKey];
    const target = pair.members.find((m) => m.id === targetId);
    const duplicate = pair.members.find((m) => m.id !== targetId);
    if (!target || !duplicate) return;

    // 충돌 필드 선택값 — 대표(target)의 현재 값과 다른 선택만 서버로 보낸다
    const choices: MemberDedupFieldChoices = {};
    const chosen = fieldChoice[pair.pairKey];
    if (chosen?.phone && (!target.phone || normalizePhone(chosen.phone) !== normalizePhone(target.phone))) {
      choices.phone = chosen.phone;
    }
    if (chosen?.email && chosen.email !== target.email) {
      choices.email = chosen.email;
    }

    setBusyPairKey(pair.pairKey);
    try {
      const result = await memberDedupApi.merge(companyId, {
        targetMemberId: target.id,
        duplicateMemberId: duplicate.id,
        ...(Object.keys(choices).length > 0 ? { fieldChoices: choices } : {}),
      });
      if (result.skippedFields.length > 0) {
        showToast(
          '병합했습니다. 일부 연락처는 다른 가입 계정과 충돌하여 채우지 않았습니다.',
          'info',
        );
      } else {
        showToast('같은 사람으로 병합했습니다.', 'success');
      }
      markProcessed(pair.pairKey, { kind: 'merged', duplicateMemberId: duplicate.id });
    } catch (err) {
      showToast(extractApiError(err).message, 'error');
    } finally {
      setBusyPairKey(null);
    }
  };

  const handleDismiss = async (pair: MemberDedupPair) => {
    setBusyPairKey(pair.pairKey);
    try {
      await memberDedupApi.dismiss(companyId, pair.members[0].id, pair.members[1].id);
      showToast('별개의 인원으로 처리했습니다.', 'success');
      markProcessed(pair.pairKey, { kind: 'dismissed' });
    } catch (err) {
      showToast(extractApiError(err).message, 'error');
    } finally {
      setBusyPairKey(null);
    }
  };

  /** 병합 되돌리기 — 링크 해제 + 멤버십 재개. 페어는 다시 판정 대기 상태로 돌아온다. */
  const handleUnmerge = async (pair: MemberDedupPair, duplicateMemberId: string) => {
    setBusyPairKey(pair.pairKey);
    try {
      await memberDedupApi.unmerge(companyId, duplicateMemberId);
      showToast('병합을 되돌렸습니다.', 'success');
      setProcessed((prev) => {
        const next = { ...prev };
        delete next[pair.pairKey];
        return next;
      });
      onChanged?.();
    } catch (err) {
      showToast(extractApiError(err).message, 'error');
    } finally {
      setBusyPairKey(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="중복 인원 정리"
      size="xl"
      overlayZClassName={overlayZClassName}
    >
      <p className="text-sm text-ink-500 mb-4">
        신원(이름·전화·이메일)이 겹치는 인원입니다. 같은 사람이면 병합하고, 동명이인 등
        별개의 인원이면 &lsquo;별개 인원&rsquo;으로 처리하세요. 병합은 이 화면에서 바로 되돌릴 수
        있습니다.
      </p>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
        </div>
      ) : pairs.length === 0 ? (
        <p className="text-sm text-ink-500 py-8 text-center">정리할 중복 인원이 없습니다.</p>
      ) : (
        <div className="space-y-4">
          {truncated && (
            <p className="text-sm text-amber-700 bg-amber-50 rounded px-3 py-2">
              후보가 많아 일부만 표시했습니다. 표시된 건을 처리한 뒤 다시 열면 나머지가
              이어서 표시됩니다.
            </p>
          )}
          {pairs.map((pair) => {
            const targetId = targetChoice[pair.pairKey];
            const busy = busyPairKey === pair.pairKey;
            const entry = processed[pair.pairKey];
            const lockedTarget = pair.members.find((m) => m.hasAccount);
            const phoneConflict =
              pair.members[0].phone &&
              pair.members[1].phone &&
              normalizePhone(pair.members[0].phone) !== normalizePhone(pair.members[1].phone);
            const emailConflict =
              pair.members[0].email &&
              pair.members[1].email &&
              pair.members[0].email !== pair.members[1].email;

            return (
              <div
                key={pair.pairKey}
                className={`rounded-card border border-ink-200 p-4 ${entry ? 'bg-ink-50' : ''}`}
              >
                <div className="mb-3 flex items-center gap-2">
                  <Badge tone="amber">{MATCHED_BY_LABEL[pair.matchedBy]}</Badge>
                  {entry && (
                    <Badge tone={entry.kind === 'merged' ? 'emerald' : 'slate'}>
                      {entry.kind === 'merged' ? '병합됨' : '별개 인원으로 처리됨'}
                    </Badge>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {pair.members.map((member) => {
                    const isTarget = member.id === targetId;
                    const selectable = (!lockedTarget || member.hasAccount) && !entry;
                    return (
                      <label
                        key={member.id}
                        className={`rounded-card border p-3 flex flex-col gap-1 ${
                          isTarget ? 'border-brand-500 bg-brand-50' : 'border-ink-200'
                        } ${selectable ? 'cursor-pointer' : 'cursor-not-allowed opacity-80'}`}
                      >
                        <span className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`dedup-target-${pair.pairKey}`}
                            checked={isTarget}
                            disabled={!selectable || busy}
                            onChange={() =>
                              setTargetChoice((prev) => ({
                                ...prev,
                                [pair.pairKey]: member.id,
                              }))
                            }
                          />
                          <span className="font-semibold text-ink-900">{member.name}</span>
                          {member.hasAccount && <Badge tone="blue">가입 계정</Badge>}
                          {isTarget && <span className="text-xs text-brand-600">대표로 유지</span>}
                        </span>
                        <span className="text-sm text-ink-600">
                          {member.phone ? formatPhone(member.phone) : '전화번호 없음'}
                        </span>
                        <span className="text-sm text-ink-600">
                          {member.email ?? '이메일 없음'}
                        </span>
                      </label>
                    );
                  })}
                </div>

                {!entry && (phoneConflict || emailConflict) && (
                  <div className="mt-3 space-y-2 text-sm">
                    <p className="text-ink-500">값이 다른 항목은 남길 값을 선택하세요.</p>
                    {phoneConflict && (
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-ink-700 font-medium">전화번호:</span>
                        {pair.members.map((m) => (
                          <label key={`${pair.pairKey}-phone-${m.id}`} className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="radio"
                              name={`dedup-phone-${pair.pairKey}`}
                              disabled={busy}
                              checked={
                                (fieldChoice[pair.pairKey]?.phone ??
                                  pair.members.find((x) => x.id === targetId)?.phone) === m.phone
                              }
                              onChange={() =>
                                setFieldChoice((prev) => ({
                                  ...prev,
                                  [pair.pairKey]: { ...prev[pair.pairKey], phone: m.phone ?? undefined },
                                }))
                              }
                            />
                            {m.phone ? formatPhone(m.phone) : '-'}
                          </label>
                        ))}
                      </div>
                    )}
                    {emailConflict && (
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-ink-700 font-medium">이메일:</span>
                        {pair.members.map((m) => (
                          <label key={`${pair.pairKey}-email-${m.id}`} className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="radio"
                              name={`dedup-email-${pair.pairKey}`}
                              disabled={busy}
                              checked={
                                (fieldChoice[pair.pairKey]?.email ??
                                  pair.members.find((x) => x.id === targetId)?.email) === m.email
                              }
                              onChange={() =>
                                setFieldChoice((prev) => ({
                                  ...prev,
                                  [pair.pairKey]: { ...prev[pair.pairKey], email: m.email ?? undefined },
                                }))
                              }
                            />
                            {m.email ?? '-'}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-4 flex items-center justify-end gap-2">
                  {entry ? (
                    entry.kind === 'merged' ? (
                      <>
                        <span className="text-sm text-ink-500 mr-auto">
                          잘못 병합했다면 지금 되돌릴 수 있습니다.
                        </span>
                        <Button
                          variant="secondary"
                          disabled={busy}
                          onClick={() => void handleUnmerge(pair, entry.duplicateMemberId)}
                        >
                          되돌리기
                        </Button>
                      </>
                    ) : (
                      <span className="text-sm text-ink-500 mr-auto">
                        별개의 인원으로 처리해 후보에서 제외했습니다.
                      </span>
                    )
                  ) : (
                    <>
                      <Button
                        variant="secondary"
                        disabled={busy}
                        onClick={() => void handleDismiss(pair)}
                      >
                        별개 인원
                      </Button>
                      <Button variant="primary" disabled={busy} onClick={() => void handleMerge(pair)}>
                        같은 사람 — 병합
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
